import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Gift, Sparkles, Copy, CheckCircle2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const SHOPIFY_URL = process.env.REACT_APP_SHOPIFY_URL || "https://topcocoon.com";

const ParrainageLanding = () => {
  const { code } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    axios.get(`${API}/parrainage/public/${code}`)
      .then((r) => setInfo(r.data))
      .catch(() => setError("Ce lien de parrainage n'est pas valide ou a expiré."));
  }, [code]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(info?.code || "");
      setCopied(true);
      toast.success("Code copié !");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Impossible de copier");
    }
  };

  const handleShopify = () => {
    const url = `${SHOPIFY_URL}/?discount=${encodeURIComponent(info?.code || "")}`;
    window.location.href = url;
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 p-8 text-center">
          <div className="text-4xl mb-3">🔍</div>
          <h1 className="text-xl font-bold text-slate-800 mb-2">Code introuvable</h1>
          <p className="text-sm text-slate-500">{error}</p>
          <a href={SHOPIFY_URL} className="inline-flex items-center gap-1 mt-4 text-indigo-600 hover:underline text-sm">
            Aller sur TopCocoon <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    );
  }

  if (!info) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4" data-testid="parrainage-landing">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-xl">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-center text-white">
            <Sparkles className="w-10 h-10 mx-auto mb-2" />
            <h1 className="text-2xl font-bold mb-1">Cadeau de {info.parrain_prenom} !</h1>
            <p className="text-indigo-100 text-sm">
              {info.parrain_prenom} vous recommande <strong>{info.brand}</strong>
            </p>
          </div>

          <div className="p-6 space-y-5">
            <div className="text-center">
              <p className="text-slate-500 text-sm mb-1">Votre avantage exclusif</p>
              <p className="text-5xl font-extrabold text-indigo-600">-{info.discount_percent}%</p>
              <p className="text-slate-500 text-xs mt-1">sur votre première commande</p>
            </div>

            <div className="bg-indigo-50 border-2 border-dashed border-indigo-300 rounded-xl p-4 text-center">
              <p className="text-xs uppercase tracking-wide text-indigo-600 font-medium">Votre code</p>
              <div className="flex items-center justify-center gap-2 mt-1">
                <p className="text-2xl font-bold text-indigo-800 font-mono" data-testid="parrainage-code">{info.code}</p>
                <button onClick={handleCopy} className="p-1.5 rounded-lg hover:bg-indigo-100 transition-colors" data-testid="copy-code-btn">
                  {copied ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <Copy className="w-5 h-5 text-indigo-500" />}
                </button>
              </div>
            </div>

            <button
              onClick={handleShopify}
              className="w-full py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold transition-colors flex items-center justify-center gap-2"
              data-testid="go-shopify-btn"
            >
              <Gift className="w-5 h-5" />
              Profiter de mon cadeau
            </button>

            <p className="text-xs text-slate-400 text-center">
              Entrez le code <strong>{info.code}</strong> au moment du paiement.<br/>
              {info.parrain_prenom} recevra un petit remerciement de notre part 💝
            </p>
          </div>
        </div>

        <p className="text-center text-xs text-slate-400 mt-4">
          Powered by {info.brand}
        </p>
      </div>
    </div>
  );
};

export default ParrainageLanding;
